/**
 * Module: Investors
 * Archetype: ACV
 */
import { useState, useEffect } from 'react';
import { Typography } from '@/lib/typography';
import { StandardCollectionPage } from '@/components/shared/StandardCollectionPage';
import { investorSchema } from '@/schemas/readViewSchemas';
import { PrimaryButton } from '@/components/shared/PrimaryButton';
import { Pill } from '@/components/ui/pill';
import { InvestorsFilterBar } from '@/components/dashboard/InvestorsFilterBar';
import { getInvestors, getInvestorCounts, createInvestor, updateInvestor, deleteInvestor } from '@/lib/api';
import { Plus, Buildings, Fire, FileText, CheckCircle, Users } from '@phosphor-icons/react';
import { UI_INVARIANTS } from '@/config/ui-invariants';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { adaptInvestor } from '@/adapters/investor.adapter';
import { InvestorRenderContract } from '@/contracts/render_contracts';
import { RuntimeContractValidator } from '@/validators/runtime_validator';
import { DashboardCard } from '@/components/dashboard/DashboardCard';
import InvestorEditForm from '@/components/investors/InvestorEditForm';
import { toast } from 'sonner';


export default function Investors() {
    // Stability: Initialize counts to prevent ReferenceError before fetch
    const [counts, setCounts] = useState({ total: 0 });
    const [investors, setInvestors] = useState([]);
    // Stability: Consume shared invariant for default entry state
    const [filters, setFilters] = useState(() => {
        const persistedStatus = UI_INVARIANTS.NAVIGATION.getPersistedTab('INVESTORS');
        return {
            sector: '',
            stage: '',
            geo: '',
            status: persistedStatus || ''
        };
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingInvestor, setEditingInvestor] = useState(null);
    const [addingInvestor, setAddingInvestor] = useState(false);
    const [deletingInvestor, setDeletingInvestor] = useState(null);
    const [saving, setSaving] = useState(false);

    // Persistence Hook: Ensure navigation re-entry respects last selection
    useEffect(() => {
        UI_INVARIANTS.NAVIGATION.setPersistedTab('INVESTORS', filters.status);
    }, [filters.status]);

    useEffect(() => {
        fetchData();
    }, [filters]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [invRes, countsRes] = await Promise.all([
                getInvestors(filters),
                getInvestorCounts()
            ]);

            // Truth Validation (Phase 3)
            // Validate ADAPTED data against Render Contract (UI Shape), not Raw Data
            const adaptedInvestors = (invRes || []).map(adaptInvestor);
            RuntimeContractValidator.validateCollection(adaptedInvestors, InvestorRenderContract, 'Investors');

            setInvestors(invRes);
            setCounts(countsRes);
        } catch (error) {
            console.error('Failed to fetch investors:', error);
            setError('Failed to load investors data.');
        } finally {
            setLoading(false);
        }
    };

    const handleKPIClick = (kpiId) => {
        // Map KPI click to status filter (Standard Archetype Logic)
        const status = kpiId === 'all' ? '' : kpiId;
        handleFilterChange('status', status);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleEditInvestor = async (investorData) => {
        setSaving(true);
        try {
            await updateInvestor(editingInvestor.id, investorData);
            toast.success('Investor updated successfully');
            setEditingInvestor(null);
            // Refresh investors list
            await fetchData();
        } catch (error) {
            console.error('Failed to update investor:', error);
            toast.error('Failed to update investor');
        } finally {
            setSaving(false);
        }
    };

    const handleAddInvestor = async (investorData) => {
        setSaving(true);
        try {
            await createInvestor(investorData);
            toast.success('Investor added successfully');
            setAddingInvestor(false);
            await fetchData();
        } catch (error) {
            console.error('Failed to add investor:', error);
            toast.error('Failed to add investor');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteInvestor = async () => {
        if (!deletingInvestor) return;
        try {
            await deleteInvestor(deletingInvestor.id);
            toast.success('Investor archived successfully');
            setDeletingInvestor(null);
            await fetchData();
        } catch (error) {
            console.error('Failed to archive investor:', error);
            toast.error('Failed to archive investor');
        }
    };

    // --- Render (Unification V2.1 - Archetype Authority) ---
    return (
        <>
            <StandardCollectionPage
                schema={investorSchema}
                schemaAdapter={adaptInvestor}
                config={{
                    title: "Investors",
                    description: `Manage funds and contacts ("${loading ? '...' : counts.total}" total)`,
                    entityName: "investors",
                    emptyTitle: "Investor Network",
                    emptyDescription: "Your LP and co-investor contacts will appear here.",
                    emptyIcon: Users,
                }}
                data={investors}
                status={loading ? 'loading' : error ? 'error' : 'success'}
                onRefresh={fetchData}

                // Filters Slot
                filters={
                    <div className="space-y-6 w-full">
                        <InvestorsFilterBar filters={filters} onFilterChange={handleFilterChange} />

                        {/* Counts Grid (Metrics) - Driven by UI_INVARIANTS */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {UI_INVARIANTS.MODULES.INVESTORS.TABS.map(tab => (
                                <div
                                    key={tab.id}
                                    onClick={() => handleKPIClick(tab.id)}
                                    className={`cursor-pointer transition-all active:scale-95 ${filters.status === (tab.id === 'all' ? '' : tab.id) ? 'ring-2 rounded-lg' : ''}`}
                                    style={filters.status === (tab.id === 'all' ? '' : tab.id) ? { ringColor: "var(--brand-primary)", backgroundColor: "var(--brand-primary-alpha)" } : {}}
                                >
                                    <DashboardCard
                                        title={tab.label}
                                        value={UI_INVARIANTS.RESOLVERS.resolveModuleCount(counts, tab.id)}
                                        icon={(() => {
                                            switch (tab.id) {
                                                case 'all': return <Buildings className="h-5 w-5" style={{ color: "var(--brand-primary)" }} /* noscan — CSS var token */ />;
                                                case 'warm': return <Fire className="h-5 w-5 text-amber-600" />;
                                                case 'negotiation': return <FileText className="h-5 w-5" style={{ color: "var(--brand-primary)" }} /* noscan — CSS var token */ />;
                                                case 'closed': return <CheckCircle className="h-5 w-5 text-slate-600" />;
                                                default: return null;
                                            }
                                        })()}
                                        color={tab.id === 'warm' ? 'bg-amber-100' : (['negotiation', 'closed'].includes(tab.id) ? 'bg-slate-50' : 'bg-brand-100')}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                }

                // Actions Slot
                actions={
                    <PrimaryButton onClick={() => setAddingInvestor(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Investor
                    </PrimaryButton>
                }

                // Card Render Logic (Ensured by Stability V2 Contracts)
                renderCard={(rawInvestor) => {
                    // Identity: Component must never touch raw API data
                    const investor = adaptInvestor(rawInvestor);

                    return (
                        <div
                            className="group flex flex-col min-h-[200px] bg-card hover:shadow-lg transition-all duration-300 border border-border rounded-lg h-full relative"
                        >
                            <div className="p-6 flex flex-col h-full">
                                {/* Header */}
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded bg-brand-50 flex items-center justify-center border border-brand-100 text-action">
                                            {investor.avatar_initial}
                                        </div>
                                        <div>
                                            <h3 className="font-heading text-foreground line-clamp-1">{investor.display_name}</h3>
                                            <p className="text-muted-foreground">{investor.subtitle}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Metrics / Info */}
                                <div className="space-y-2 mb-4 flex-grow">
                                    <div className="flex flex-wrap gap-1 mb-2">
                                        {investor.sectors.slice(0, 2).map((s, i) => (
                                            <Pill key={`${s}-${i}`} variant="secondary" className="text-[10px]">
                                                {s}
                                            </Pill>
                                        ))}
                                        {investor.sectors.length > 2 && <span className="text-[10px] text-muted-foreground">+{investor.sectors.length - 2}</span>}
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Geo:</span>
                                        <span className="text-foreground">{investor.geo_label}</span>
                                    </div>
                                </div>

                                {/* Footer Actions */}
                                <div className="pt-4 border-t border-border flex justify-end opacity-0 group-hover:opacity-100 transition-opacity gap-2">
                                    <button
                                        className="hover:underline text-action"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.stopPropagation();
                                            setEditingInvestor(rawInvestor);
                                        }}
                                    >
                                        Edit
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                }}
            />

            {/* --- Modals --- */}
            <Dialog open={!!editingInvestor} onOpenChange={() => setEditingInvestor(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Investor</DialogTitle>
                        <DialogDescription>
                            Update investor information
                        </DialogDescription>
                    </DialogHeader>
                    {editingInvestor && (
                        <InvestorEditForm
                            investor={editingInvestor}
                            onSave={handleEditInvestor}
                            onCancel={() => setEditingInvestor(null)}
                            saving={saving}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Add Investor Modal */}
            <Dialog open={!!addingInvestor} onOpenChange={setAddingInvestor}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add New Investor</DialogTitle>
                        <DialogDescription>
                            Add a new investor to the database
                        </DialogDescription>
                    </DialogHeader>
                    <InvestorEditForm
                        investor={{}}
                        onSave={handleAddInvestor}
                        onCancel={() => setAddingInvestor(false)}
                        saving={saving}
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deletingInvestor} onOpenChange={() => setDeletingInvestor(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Archive Investor</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to archive "{deletingInvestor ? adaptInvestor(deletingInvestor).display_name : ''}"? This will set it as inactive (soft delete).
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteInvestor}>
                            Archive
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
